import { Link } from "react-router-dom";

function CostCard({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div className="border-y-2 border-foreground-900 bg-white px-4 py-5">
      <span className="text-xs font-semibold text-foreground-500">{label}</span>
      <strong className="mt-2 block text-2xl text-primary-700">{value}</strong>
      <p className="mb-0 mt-2 text-sm leading-6 text-foreground-700">{note}</p>
    </div>
  );
}

export default function AreYouOverpayingArticle() {
  return (
    <>
      <section id="intro">
        <p className="text-[1.08em] leading-[1.9] text-foreground-900">
          「スマホ代って、こんなものだよね」。そう思って、何年も同じ料金を払い続けていませんか。
        </p>
        <p>携帯電話の料金は大きく下がり、選択肢も増えました。それでも、自分の請求額は契約したときのまま。そんな人が少なくありません。</p>
        <aside><strong>料金は、自動では下がりません。</strong><p>新しいプランが出ても、契約中の内容が自動で最適なものへ変わるわけではありません。一度だけ中身を分けて見る必要があります。</p></aside>
      </section>

      <section id="forty-years">
        <h2>40年前の携帯電話は、月額2万6,000円から始まった</h2>
        <p>1985年に登場したショルダーホンは、重量約3kg。NTTグループの歴史にも、車から離れて使える画期的な電話として残っています。</p>
        <p>当時の案内では、新規加入料8万円、保証金20万円、月額基本料2万6,000円。現在の平均月額4,356円と単純比較すると、基本料・月額利用料は約8割低い水準です。</p>
        <p>ただし、1985年と現在ではサービス内容、端末、通話、データ通信が大きく異なります。「同じ商品が8割値下がりした」という意味ではなく、携帯電話を持つための費用が桁違いに下がった歴史として見てください。</p>
        <div className="not-prose my-7 grid gap-4 md:grid-cols-2">
          <CostCard label="1985年・ショルダーホン" value="月額基本料 26,000円" note="新規加入料8万円、保証金20万円。通話料は別。" />
          <CostCard label="2025年1月・スマホ平均" value="月額 4,356円" note="MM総研調査。端末代の分割支払いを除く。" />
        </div>
      </section>

      <section id="current-average">
        <h2>現在も、選ぶ場所で平均額に差がある</h2>
        <p>MM総研の2025年1月調査では、スマートフォンの月額利用料金は平均4,356円でした。内訳を見ると、契約先の種類で差があります。</p>
        <div className="not-prose my-7 overflow-x-auto border-y-2 border-foreground-900 bg-white">
          <table className="w-full min-w-[560px] border-collapse text-left text-sm">
            <thead className="bg-background-100"><tr><th className="p-3">契約先</th><th className="p-3">平均月額</th><th className="p-3">平均との差</th></tr></thead>
            <tbody>
              <tr className="border-t border-background-300"><th className="p-3">MNO 4ブランド</th><td className="p-3 font-bold">5,025円</td><td className="p-3">＋669円</td></tr>
              <tr className="border-t border-background-300"><th className="p-3">サブブランド</th><td className="p-3 font-bold">3,184円</td><td className="p-3">−1,172円</td></tr>
              <tr className="border-t border-background-300"><th className="p-3">MVNO</th><td className="p-3 font-bold">1,961円</td><td className="p-3">−2,395円</td></tr>
            </tbody>
          </table>
        </div>
        <p>MNOとMVNOの平均差は月3,064円、年換算で36,768円です。これは個人ごとの節約額ではありませんが、見直し余地を探す目安になります。</p>
      </section>

      <section id="price-turn">
        <h2>2026年は「下がり続ける」から「上がる会社もある」へ</h2>
        <p>2026年には、大手各社で新プランや料金改定が続きました。ソフトバンクは7月から既存プランを月110〜550円改定。ドコモやauも、付帯サービスを増やした大容量プランを提供しています。</p>
        <p>料金だけが理由ではなく、衛星通信、海外利用、動画サービスなどが含まれるプランもあります。使う人には価値がありますが、使わない人は「付いたサービス」ではなく、毎月払う総額を確認する必要があります。</p>
        <aside><strong>これからは、同じ会社に長くいれば自然に安くなるとは限りません。</strong><p>値上げ、新サービス、割引終了が重なるほど、見直す人とそのままの人の差が広がります。</p></aside>
      </section>

      <section id="five-leaks">
        <h2>見直していない人が払い続けやすい5つ</h2>
        <div className="not-prose my-7 border-y-2 border-foreground-900 bg-white">
          {[
            ["1. 古い基本料", "受付終了プランが必ず損とは限りません。ただし、今の使い方と合っているかは別問題です。"],
            ["2. 使っていないギガ", "20GB契約でも実際は月5〜7GBなら、毎月の余りに料金を払っている可能性があります。"],
            ["3. 惰性のオプション", "補償、動画、クラウド、セキュリティなど、月数百円でも複数あれば年単位で大きくなります。"],
            ["4. 端末代の連続", "返却型を使い続けると、買い替えのたびに分割払いが続きます。所有する場合との総額を比べます。"],
            ["5. 割引終了後の料金", "期間限定割引が終わると、手続きなしで通常料金へ戻る場合があります。請求額の変化を確認します。"],
          ].map(([title, text], index) => (
            <div key={title} className={`grid gap-2 px-2 py-5 md:grid-cols-[190px_1fr] md:gap-7 md:px-4 ${index ? "border-t border-background-300" : ""}`}>
              <strong className="text-[16px] leading-7 text-foreground-900">{title}</strong>
              <p className="m-0 text-[15px] leading-7 text-foreground-700">{text}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="not-only-cheap">
        <h2>でも、「安ければ正解」ではない</h2>
        <p>最安プランだけを見て動くと、別の費用や不便が増えることがあります。</p>
        <ul>
          <li>容量を下げすぎて、毎月速度制限になる</li>
          <li>電話をよく使うのに、通話料を計算していない</li>
          <li>自分の乗り換えで、家族や自宅回線の割引が外れる</li>
          <li>店頭で相談できると思って、オンライン専用プランを選ぶ</li>
          <li>端末が対応していない、データ移行で困る</li>
        </ul>
        <p>最初に「格安SIMへ変える」と決めず、いま何にいくら払っているかを分けます。</p>
      </section>

      <section id="three-actions">
        <h2>今日からできることは、3つだけ</h2>
        <ol>
          <li><strong>請求を4つに分ける：</strong>基本料、端末代、通話料、オプションを別々にメモする。</li>
          <li><strong>3か月の使用量を見る：</strong>契約GBではなく、実際に使った量を確認する。</li>
          <li><strong>オプションを1つ確認する：</strong>解約前に補償や家族への影響を確認し、今も必要か判断する。</li>
        </ol>
        <p>この3つだけで、「通信費が高いのか」「端末代が高いのか」「使っていない契約があるのか」が見えます。</p>
      </section>

      <section id="author-view">
        <h2>通信業界25年の現場経験から</h2>
        <p>運営者は、キャリアショップの店員、店長、35人を束ねるエリアマネージャー、社員教育の責任者を経験してきました。</p>
        <p>その経験から言えるのは、「乗り換えた方がよい人」だけでなく、「いまは動かない方がよい人」もいるということです。家族割、端末残債、サポートの必要性を確認し、世帯全体で判断します。</p>
      </section>

      <section id="sources">
        <h2>出典・確認資料</h2>
        <ul>
          <li><a href="https://group.ntt/jp/group/history/" target="_blank" rel="noreferrer">NTTグループの歩み：1985年のショルダーホン ↗</a></li>
          <li><a href="https://note.com/ntt_inc/n/ne69d96e59cb4" target="_blank" rel="noreferrer">NTT公式note：ショルダーホン当時の料金 ↗</a></li>
          <li><a href="https://www.m2ri.jp/report/md/category.html?id=2" target="_blank" rel="noreferrer">MM総研：携帯電話の月額利用料金調査 ↗</a></li>
          <li><a href="https://www.softbank.jp/mobile/price_plan/renewal-2026/" target="_blank" rel="noreferrer">ソフトバンク：2026年料金改定 ↗</a></li>
          <li><a href="https://www.docomo.ne.jp/charge/docomo_max/" target="_blank" rel="noreferrer">ドコモ MAX ↗</a></li>
          <li><a href="https://www.au.com/mobile/charge/smartphone/plan/data-unlimited-plus/" target="_blank" rel="noreferrer">au 使い放題MAX＋ ↗</a></li>
        </ul>
        <p>確認日：2026年9月20日。平均額はMM総研の2025年1月調査で、現在の個別料金を示すものではありません。</p>
      </section>

      <section id="next-actions">
        <h2>まず、いまのスマホ代を分ける</h2>
        <p>相場を見た後は、自分の請求へ戻ります。合計額を4つに分けると、見直す場所が分かります。</p>
        <div>
          <Link to="/bill-check"><small>入力はブラウザ内だけ</small><strong>請求内訳ツールを使う</strong><span>→</span></Link>
          <Link to="/articles/which-plan-is-cheapest"><small>容量別の実額を確認</small><strong>結局どこが安いかを見る</strong><span>→</span></Link>
        </div>
      </section>
    </>
  );
}


